// Service Master API service
// Integrates with wht-mt-admin backend APIs for CRUD, list, and download.

import {
    MT_DATA_MASTER_BULK_DELETE_API,
    MT_DATA_MASTER_BULK_EDIT_API,
    MT_DATA_MASTER_CREATE_API,
    MT_DATA_MASTER_DELETE_API,
    MT_DATA_MASTER_DOWNLOAD_API,
    MT_DATA_MASTER_LIST_API,
    MT_DATA_MASTER_UPDATE_API,
    MT_MASTER_META_DATA,
    MT_SERVICE_MASTER_BULK_EDIT_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
    mtBuildRequestHeader,
    mtHandleErrorResponse,
    mtHandleRequestFailure,
    mtParseJsonResponse,
    mtParseMessageResponse,
    mtUrlBuilder,
    type MTApiResponse,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
    MTMetadataField,
    MTMetadataFormField,
    MTMetadataResponse,
    MTMetadataTableColumn,
    MTServiceMasterListRequest,
    MTServiceMasterListResponse,
    MTServiceMasterRecord,
} from '@/multi-territory/mt-types/mt-metadata'
import { sanitizeFilename } from '@/multi-territory/mt-utils/mt-sanitize'
import { formatMTHeaderLabel } from '../mt-utils/mt-search-fields'

// --- Request types ---

export interface ServiceMasterCreateRequest {
  [key: string]: string | number | null | undefined
}

export interface ServiceMasterUpdateRequest {
  [key: string]: string | number | null | undefined
}

export interface ServiceMasterDownloadRequest {
  searchCOList?: Array<{
    fieldName: string
    operator: string
    value?: string
  }>
}

// --- Helper: Build MT-specific headers ---

/**
 * Builds extra headers required by service-master APIs.
 * X-Country-Code is always required.
 *
 * Identifier logic (driven by tanApplicable from the TAN API response):
 * - tanApplicable=true  -> caller passes tanMasterUuid, set as X-Tan-Master-Id
 * - tanApplicable=false -> caller passes entityMasterUuid, set as X-Entity-Master-Id
 */
function buildServiceMasterHeaders(
  countryCode: string
): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Country-Code': countryCode,
  }
  return headers
}

/**
 * Builds the identifier fields to inject into request payloads.
 * - tanApplicable=true  -> { tanMasterUuid: identifierUuid, entityMasterUuid: null }
 * - tanApplicable=false -> { entityMasterUuid: identifierUuid, tanMasterUuid: null }
 */
function buildIdentifierPayload(
  identifierUuid: string,
  options?: { tanApplicable?: boolean }
): { tanMasterUuid: string | null; entityMasterUuid: string | null } {
  const tanApplicable = options?.tanApplicable ?? true
  if (tanApplicable) {
    return { tanMasterUuid: identifierUuid, entityMasterUuid: null }
  }
  return { entityMasterUuid: identifierUuid, tanMasterUuid: null }
}

// --- API Functions ---

/**
 * Fetches metadata for a given data type and country.
 * Returns the field definitions used to render table columns and form fields.
 * GET /wht-mt-admin/api/v1/data/{countryCode}/{dataType}/metadata
 */
export async function fetchServiceMasterMetadata(
  countryCode: string,
  dataType: string = 'SERVICE_MASTER'
): Promise<MTApiResponse<MTMetadataResponse['data']>> {
  try {
    const endpoint = MT_MASTER_META_DATA(countryCode, dataType)
    const url = mtUrlBuilder(endpoint)
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<MTMetadataResponse['data']>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetches service master data list with pagination and optional search filters.
 * POST /wht-mt-admin/api/v1/service-master/list
 *
 * Headers: X-Country-Code + X-Tan-Master-Id or X-Entity-Master-Id (based on tanApplicable)
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines which header to set (default: true)
 */
export async function fetchServiceMasterList(
  countryCode: string,
  identifierUuid: string,
  payload: MTServiceMasterListRequest,
  dataType: string = 'service_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<MTServiceMasterListResponse>> {
  try {
    const url = mtUrlBuilder(MT_DATA_MASTER_LIST_API(dataType))
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const identifierFields = buildIdentifierPayload(identifierUuid, options)
    const enrichedPayload = { ...payload, ...identifierFields }
    const requestInit = mtBuildRequestHeader(
      'POST',
      enrichedPayload,
      extraHeaders
    )
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<MTServiceMasterListResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Creates a new service master record.
 * POST /wht-mt-admin/api/v1/service-master
 *
 * Headers: X-Country-Code + X-Tan-Master-Id or X-Entity-Master-Id (based on tanApplicable)
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines which header to set (default: true)
 */
export async function createServiceMasterRecord(
  countryCode: string,
  identifierUuid: string,
  record: ServiceMasterCreateRequest,
  dataType: string = 'service_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<string>> {
  try {
    const url = mtUrlBuilder(MT_DATA_MASTER_CREATE_API(dataType))
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const identifierFields = buildIdentifierPayload(identifierUuid, options)
    const enrichedRecord = { ...record, ...identifierFields }
    const requestInit = mtBuildRequestHeader(
      'POST',
      enrichedRecord,
      extraHeaders
    )
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Updates an existing service master record.
 * PUT /wht-mt-admin/api/v1/service-master/{uuid}
 *
 * Headers: X-Country-Code + X-Tan-Master-Id or X-Entity-Master-Id (based on tanApplicable)
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines which header to set (default: true)
 */
export async function updateServiceMasterRecord(
  countryCode: string,
  identifierUuid: string,
  uuid: string,
  record: ServiceMasterUpdateRequest,
  dataType: string = 'service_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<string>> {
  try {
    const endpoint = MT_DATA_MASTER_UPDATE_API(dataType, uuid)
    const url = mtUrlBuilder(endpoint)
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const identifierFields = buildIdentifierPayload(identifierUuid, options)
    const enrichedRecord = { ...record, ...identifierFields }
    const requestInit = mtBuildRequestHeader(
      'PUT',
      enrichedRecord,
      extraHeaders
    )
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads service master data as Excel file.
 * POST /wht-mt-admin/api/v1/service-master/download
 *
 * Headers: X-Country-Code + X-Tan-Master-Id or X-Entity-Master-Id (based on tanApplicable)
 * Body: optional searchCOList for filtering
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines which header to set (default: true)
 */
export async function downloadServiceMaster(
  countryCode: string,
  identifierUuid: string,
  payload?: ServiceMasterDownloadRequest,
  dataType: string = 'service_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<Blob>> {
  try {
    const url = mtUrlBuilder(MT_DATA_MASTER_DOWNLOAD_API(dataType))
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const identifierFields = buildIdentifierPayload(identifierUuid, options)
    const enrichedPayload = { ...(payload ?? {}), ...identifierFields }
    const requestInit = mtBuildRequestHeader(
      'POST',
      enrichedPayload,
      extraHeaders
    )
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Deletes a service master/keyword record.
 * DELETE /wht-mt-admin/api/v1/{dataType}/{uuid}
 *
 * Headers: X-Country-Code + X-Tan-Master-Id or X-Entity-Master-Id (based on tanApplicable)
 *
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid (when false)
 * @param options.tanApplicable - determines which header to set (default: true)
 */
export async function deleteServiceMasterRecord(
  countryCode: string,
  uuid: string,
  dataType: string = 'service-master',
  identifierUuid?: string,
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<string>> {
  try {
    const endpoint = MT_DATA_MASTER_DELETE_API(dataType, uuid)
    const url = mtUrlBuilder(endpoint)
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const payload = identifierUuid
      ? buildIdentifierPayload(identifierUuid, options)
      : undefined
    const requestInit = mtBuildRequestHeader('DELETE', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Bulk edit records.
 * POST /wht-mt-admin/api/v1/{dataType}/edit
 * Body: { tanMasterUuid, entityMasterUuid, records: [{uuid, ...changedFields}, ...] }
 *
 * Headers: X-Country-Code
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid
 * @param options.tanApplicable - determines identifier fields (default: true)
 */
export async function bulkEditServiceMasterRecords(
  countryCode: string,
  identifierUuid: string,
  records: Array<{ uuid: string; [key: string]: any }>,
  dataType: string = 'payee_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<string>> {
  try {
    const endpoint = MT_DATA_MASTER_BULK_EDIT_API(dataType)
    const url = mtUrlBuilder(endpoint)
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const identifierFields = buildIdentifierPayload(identifierUuid, options)
    const payload = { ...identifierFields, entries: records }
    const requestInit = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export interface ServiceMasterBulkEntry {
  uuid: string
  [key: string]: any
}

/**
 * Bulk update Service Master records for ONE entity (table-wide inline edit).
 * PUT /wht-mt-admin/api/v1/service-master/bulk
 * Body: { entityMasterUuid | tanMasterUuid, entries: [{ uuid, ...fullRecord }, ...] }
 * Each entry carries the complete record — every metadata field (original row merged
 * with edits) — the same field set as single-row update. List-only keys
 * (global_uuid, associated_uuid, is_associated, audit fields) must not be sent.
 * Success: { success: true, data: [{ uuid, ...updatedFields }, ...] } (same order as sent)
 * Error (400): { success: false, errors: [...] } — all-or-nothing, nothing was saved.
 *
 * Headers: X-Country-Code
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid
 * @param options.tanApplicable - determines which identifier is sent (default: true)
 */
export async function bulkUpdateServiceMasterRecords(
  countryCode: string,
  identifierUuid: string,
  records: ServiceMasterBulkEntry[],
  dataType: string = 'service_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<ServiceMasterBulkEntry[]>> {
  if (!records.length) {
    return { success: false, error: 'No entries provided for bulk update' }
  }
  const missingIdx = records.findIndex(r => !r.uuid || !String(r.uuid).trim())
  if (missingIdx !== -1) {
    return {
      success: false,
      error: `Missing uuid for entry at index ${missingIdx}`,
    }
  }

  try {
    const url = mtUrlBuilder(MT_SERVICE_MASTER_BULK_EDIT_API(dataType))
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    // Contract: send exactly ONE identifier for the whole (single-entity) batch.
    const tanApplicable = options?.tanApplicable ?? true
    const identifierField = tanApplicable
      ? { tanMasterUuid: identifierUuid }
      : { entityMasterUuid: identifierUuid }
    const payload = { ...identifierField, entries: records }
    const requestInit = mtBuildRequestHeader('PUT', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<ServiceMasterBulkEntry[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Bulk delete records.
 * POST /wht-mt-admin/api/v1/{dataType}/delete
 * Body: { tanMasterUuid, entityMasterUuid, uuids: ["uuid-1", "uuid-2"] }
 *
 * Headers: X-Country-Code
 * @param identifierUuid - tanMasterUuid (when tanApplicable=true) or entityMasterUuid
 * @param options.tanApplicable - determines identifier fields (default: true)
 */
export async function bulkDeleteServiceMasterRecords(
  countryCode: string,
  identifierUuid: string,
  uuids: string[],
  dataType: string = 'payee_master',
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<string>> {
  try {
    const endpoint = MT_DATA_MASTER_BULK_DELETE_API(dataType)
    const url = mtUrlBuilder(endpoint)
    const extraHeaders = buildServiceMasterHeaders(countryCode)
    const identifierFields = buildIdentifierPayload(identifierUuid, options)
    const payload = { ...identifierFields, uuids }
    const requestInit = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
/**
 * Deletes a data master record (soft-delete).
 * DELETE /wht-mt-admin/api/v1/{dataType}/{uuid}
 *
 * Headers: X-Country-Code (required)
 */
export async function deleteDtaaMasterRecord(
  countryCode: string,
  uuid: string,
  dataType: string = 'dtaa_master'
): Promise<MTApiResponse<string>> {
  try {
    const endpoint = MT_DATA_MASTER_DELETE_API(dataType, uuid)
    const url = mtUrlBuilder(endpoint)
    const extraHeaders: Record<string, string> = {
      'X-Country-Code': countryCode,
    }
    const requestInit = mtBuildRequestHeader('DELETE', null, extraHeaders)
    const response = await fetch(url, requestInit)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
// --- Legacy compatibility export ---

/**
 * @deprecated Use createServiceMasterRecord instead.
 * Kept for backward compatibility with existing form submission.
 */
export async function saveServiceMasterRecord(
  countryCode: string,
  identifierUuid: string,
  record: MTServiceMasterRecord,
  options?: { tanApplicable?: boolean }
): Promise<MTApiResponse<string>> {
  return createServiceMasterRecord(
    countryCode,
    identifierUuid,
    record,
    'service-master',
    options
  )
}

// --- Utility Functions ---

/**
 * Converts metadata fields to table columns.
 * Filters for visible fields and sorts by order.
 */
export function metadataToTableColumns(
  fields: MTMetadataField[] | null | undefined
): MTMetadataTableColumn[] {
  if (!Array.isArray(fields)) return []
  return (
    fields
      .filter(f => f.visible)
      .map((f, idx) => ({ f, idx }))
      // Sort by order when present; fall back to original position so grouping
      // stays stable even if the API omits or duplicates 'order'.
      .sort((a, b) => {
        const ao = typeof a.f.order === 'number' ? a.f.order : a.idx
        const bo = typeof b.f.order === 'number' ? b.f.order : b.idx
        return ao - bo || a.idx - b.idx
      })
      .map(({ f }) => {
        // The API may send column_group as a plain string (label) or a full object.
        // Normalize to { key, label } so MTCommonTable can build spanning headers.
        // Format every API-provided label as running text (sentence case,
        // acronyms such as ID/TIN/DTAA kept uppercase) so metadata-driven column
        // headers match the rest of the MT UI.
        const cg = f.column_group
        const columnGroup = cg
          ? typeof cg === 'string'
            ? { key: cg, label: formatMTHeaderLabel(cg) }
            : { ...cg, label: formatMTHeaderLabel(cg.label) }
          : null

        return {
          id: f.field_key,
          label: formatMTHeaderLabel(f.label),
          type: f.type,
          sortable: true,
          // System-generated columns are read-only in the grid
          editable: f.editable && !f.system_generated,
          columnGroup,
          options: f.options,
        }
      })
  )
}

/**
 * Converts metadata fields to form fields.
 * All visible, non-system fields are shown in the Add/Edit form.
 * Non-editable fields (editable: false) are still rendered but disabled/read-only
 * by MTFormField — they must not be dropped from the form.
 */
export function metadataToFormFields(
  fields: MTMetadataField[] | null | undefined
): MTMetadataFormField[] {
  if (!Array.isArray(fields)) return []
  return fields
    .filter(f => f.visible)
    .sort((a, b) => a.order - b.order)
    .map(f => {
      let controlType: MTMetadataFormField['controlType'] = 'text'

      if (f.type === 'date') {
        controlType = 'date'
      } else if (f.type === 'number') {
        controlType = 'number'
      } else if (f.type === 'dropdown') {
        controlType = 'dropdown'
      } else if (f.type === 'multi-select') {
        controlType = 'multiselect'
      }

      return {
        key: f.field_key,
        label: f.label,
        type: f.type,
        required: f.required,
        editable: f.editable,
        placeholder: f.placeholder,
        validations: f.validations,
        controlType,
        options: f.options,
        gridColumn: f.grid_column,
        order: f.order,
        dateFormat: f.date_format,
      }
    })
}

/**
 * Triggers a file download from a Blob response.
 * Creates a temporary anchor element to initiate browser download.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  // CWE-80: the filename is untrusted (server Content-Disposition / row data /
  // user input). It is neutralized up front and the download anchor is NEVER
  // inserted into the document — we click a detached element — so no tainted
  // value ever reaches a `Node.appendChild` DOM sink.
  const safeFilename = sanitizeFilename(filename)
  const url = window.URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = safeFilename
    anchor.rel = 'noopener'
    // Detached click — no appendChild / removeChild on document.body.
    anchor.click()
  } finally {
    window.URL.revokeObjectURL(url)
  }
}
